import React, { useState, useEffect, useCallback } from "react";
import {
  Landmark, Upload, FileText, CheckCircle2, Trash2, ArrowLeft, AlertTriangle, Sparkles,
} from "lucide-react";
import { Card, Button, Badge, Spinner, PageHeader, Select } from "../../lib/ui";
import { useT } from "../../lib/i18n";
import { useToast } from "../../lib/toast";
import { useAuth } from "../../context/AuthContext";
import api from "../../lib/api";
import { rp } from "../../components/finance";

const STATUS_TONE = { DRAFT: "warn", APPROVED: "success" };
const TXN_TONE = { AUTO: "success", REVIEWED: "success", NEEDS_REVIEW: "warn", UNKNOWN: "danger" };

// ---------------- Upload + list ----------------
function StatementList({ onOpen, canUpload }) {
  const { t } = useT();
  const toast = useToast();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [accounts, setAccounts] = useState([]);
  const [acctIdx, setAcctIdx] = useState(0);
  const [bankName, setBankName] = useState("BCA");
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, a] = await Promise.all([api.get("/finance/bank/statements"), api.get("/finance/bank/accounts")]);
      setRows(s.data.rows || []);
      setAccounts(a.data.accounts || []);
    } catch { toast.error(t("Failed to load")); } finally { setLoading(false); }
  }, [t, toast]);
  useEffect(() => { load(); }, [load]);

  const upload = async (file) => {
    if (!file) return;
    const acct = accounts[acctIdx] || { type: "CENTRAL" };
    const fd = new FormData();
    fd.append("file", file);
    fd.append("account_type", acct.type);
    if (acct.branch_id) fd.append("branch_id", acct.branch_id);
    if (bankName) fd.append("bank_name", bankName);
    setUploading(true);
    try {
      const { data } = await api.post("/finance/bank/upload", fd, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
      toast.success(t("Statement parsed"));
      onOpen(data.statement.id);
    } catch (e) { toast.error(e?.response?.data?.detail || t("Upload failed")); }
    finally { setUploading(false); }
  };

  return (
    <div className="space-y-5">
      {canUpload && (
        <Card data-testid="bank-upload-card">
          <h3 className="mb-3 flex items-center gap-2 font-heading font-bold text-ink"><Upload size={18} /> {t("Upload Bank Statement")}</h3>
          <div className="grid gap-3 sm:grid-cols-3">
            <Select label={t("Account")} value={acctIdx} onChange={(e) => setAcctIdx(Number(e.target.value))} data-testid="bank-account-select">
              {accounts.map((a, i) => <option key={i} value={i}>{a.label}</option>)}
            </Select>
            <div>
              <label className="cf-label">{t("Bank Name")}</label>
              <input className="cf-input" value={bankName} onChange={(e) => setBankName(e.target.value)} placeholder="BCA" data-testid="bank-name-input" />
            </div>
            <div className="flex items-end">
              <label className="w-full">
                <input type="file" accept=".csv,.xls,.xlsx,.pdf" className="hidden" data-testid="bank-file-input"
                  onChange={(e) => upload(e.target.files?.[0])} disabled={uploading} />
                <span className={`flex cursor-pointer items-center justify-center gap-2 rounded-xl border-2 border-dashed border-primary/40 bg-primary-light px-4 py-2.5 text-sm font-semibold text-primary ${uploading ? "opacity-60" : "hover:bg-primary/10"}`}>
                  {uploading ? <Spinner /> : <Upload size={16} />} {uploading ? t("Parsing…") : t("Choose file")}
                </span>
              </label>
            </div>
          </div>
          <p className="mt-2 text-[11px] text-ink-mute">{t("Supported: CSV, XLS, XLSX, PDF. AI detects and categorizes transactions automatically.")}</p>
        </Card>
      )}

      <Card data-testid="bank-statements-list">
        <h3 className="mb-3 flex items-center gap-2 font-heading font-bold text-ink"><FileText size={18} /> {t("Statements")}</h3>
        {loading ? <div className="flex h-32 items-center justify-center"><Spinner className="h-7 w-7" /></div>
          : rows.length === 0 ? <p className="py-8 text-center text-sm text-ink-mute">{t("No statements yet")}</p>
            : (
              <div className="divide-y divide-line">
                {rows.map((s) => (
                  <button key={s.id} onClick={() => onOpen(s.id)} data-testid={`bank-statement-${s.id}`}
                    className="flex w-full items-center justify-between gap-3 py-3 text-left hover:bg-stone-50">
                    <div className="min-w-0">
                      <p className="truncate font-semibold text-ink">{s.account_label}</p>
                      <p className="text-xs text-ink-mute">{s.filename} · {s.txn_count} {t("transactions")} · {s.period_start || "—"} → {s.period_end || "—"}</p>
                    </div>
                    <div className="flex shrink-0 items-center gap-3">
                      <div className="text-right text-xs">
                        <p className="text-success-text">+{rp(s.total_in)}</p>
                        <p className="text-danger-text">-{rp(s.total_out)}</p>
                      </div>
                      <Badge tone={STATUS_TONE[s.status]}>{s.status === "APPROVED" ? t("Approved") : t("Draft")}</Badge>
                    </div>
                  </button>
                ))}
              </div>
            )}
      </Card>
    </div>
  );
}

// ---------------- Statement detail / review ----------------
function StatementDetail({ id, onBack, canApprove }) {
  const { t } = useT();
  const toast = useToast();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cats, setCats] = useState([]);
  const [approving, setApproving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [d, c] = await Promise.all([api.get(`/finance/bank/statements/${id}`), api.get("/finance/bank/categories")]);
      setData(d.data); setCats(c.data.categories || []);
    } catch { toast.error(t("Failed to load")); } finally { setLoading(false); }
  }, [id, t, toast]);
  useEffect(() => { load(); }, [load]);

  const approved = data?.statement?.status === "APPROVED";

  const updateTxn = async (txnId, body) => {
    try {
      const { data: txn } = await api.patch(`/finance/bank/transactions/${txnId}`, body);
      setData((d) => ({ ...d, transactions: d.transactions.map((x) => x.id === txnId ? txn : x) }));
    } catch (e) { toast.error(e?.response?.data?.detail || t("Failed")); }
  };

  const approve = async () => {
    setApproving(true);
    try {
      const { data: res } = await api.post(`/finance/bank/statements/${id}/approve`);
      toast.success(`${t("Approved")} · ${res.entries_created} ${t("entries created")}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || t("Approve failed")); }
    finally { setApproving(false); }
  };

  const remove = async () => {
    try { await api.delete(`/finance/bank/statements/${id}`); toast.success(t("Deleted")); onBack(); }
    catch (e) { toast.error(e?.response?.data?.detail || t("Failed")); }
  };

  if (loading || !data) return <div className="flex h-64 items-center justify-center"><Spinner className="h-8 w-8" /></div>;
  const s = data.statement;
  const needs = data.transactions.filter((x) => (x.status === "NEEDS_REVIEW" || x.status === "UNKNOWN") && !x.skip).length;

  return (
    <div className="space-y-4" data-testid="bank-statement-detail">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <Button variant="outline" size="sm" onClick={onBack} data-testid="bank-back-btn"><ArrowLeft size={15} /> {t("Back")}</Button>
        <div className="flex items-center gap-2">
          {!approved && <Button variant="ghost" size="sm" onClick={remove} data-testid="bank-delete-btn" className="text-danger"><Trash2 size={15} /> {t("Delete")}</Button>}
          {!approved && canApprove && (
            <Button onClick={approve} loading={approving} data-testid="bank-approve-btn"><CheckCircle2 size={16} /> {t("Approve & Create Entries")}</Button>
          )}
          {approved && <Badge tone="success" data-testid="bank-approved-badge">{t("Approved")} · {s.entries_created} {t("entries")}</Badge>}
        </div>
      </div>

      <Card>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h3 className="font-heading text-lg font-bold text-ink">{s.account_label}</h3>
            <p className="text-xs text-ink-mute">{s.filename} · {s.txn_count} {t("transactions")}</p>
          </div>
          <div className="flex gap-5 text-sm">
            <div><p className="text-xs text-ink-mute">{t("Money In")}</p><p className="font-bold text-success-text">+{rp(s.total_in)}</p></div>
            <div><p className="text-xs text-ink-mute">{t("Money Out")}</p><p className="font-bold text-danger-text">-{rp(s.total_out)}</p></div>
          </div>
        </div>
        {!approved && needs > 0 && (
          <div className="mt-3 flex items-center gap-2 rounded-lg bg-warn-light px-3 py-2 text-sm text-warn-text" data-testid="bank-review-warning">
            <AlertTriangle size={15} /> {needs} {t("transactions need review before approval")}
          </div>
        )}
      </Card>

      <Card className="overflow-x-auto" data-testid="bank-transactions-table">
        <table className="w-full text-sm">
          <thead className="border-b border-line text-left text-xs uppercase text-ink-mute">
            <tr>
              <th className="py-2 pr-3">{t("Date")}</th>
              <th className="py-2 pr-3">{t("Description")}</th>
              <th className="py-2 pr-3 text-right">{t("Amount")}</th>
              <th className="py-2 pr-3">{t("Category")}</th>
              <th className="py-2 pr-3 text-center">{t("Status")}</th>
              <th className="py-2 pr-3 text-center">{t("Skip")}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {data.transactions.map((x) => {
              const opts = cats.filter((c) => c.income === (x.direction === "CREDIT"));
              return (
                <tr key={x.id} className={x.skip ? "opacity-50" : ""} data-testid={`bank-txn-${x.id}`}>
                  <td className="py-2.5 pr-3 text-ink-soft">{x.date || "—"}</td>
                  <td className="py-2.5 pr-3 text-ink">{x.description}</td>
                  <td className={`py-2.5 pr-3 text-right font-semibold ${x.direction === "CREDIT" ? "text-success-text" : "text-danger-text"}`}>{x.direction === "CREDIT" ? "+" : "-"}{rp(x.amount)}</td>
                  <td className="py-2.5 pr-3">
                    <select
                      className="cf-input !h-8 !py-1 text-xs disabled:opacity-60"
                      value={x.category_slug || ""}
                      disabled={approved || x.skip}
                      data-testid={`bank-txn-category-${x.id}`}
                      onChange={(e) => updateTxn(x.id, { category_slug: e.target.value })}
                    >
                      {opts.map((c) => <option key={c.slug} value={c.slug}>{c.label}</option>)}
                    </select>
                  </td>
                  <td className="py-2.5 pr-3 text-center">
                    <Badge tone={TXN_TONE[x.status] || "mute"} className="!text-[10px]">{x.status}</Badge>
                  </td>
                  <td className="py-2.5 pr-3 text-center">
                    <input type="checkbox" className="h-4 w-4 accent-primary" checked={!!x.skip} disabled={approved}
                      data-testid={`bank-txn-skip-${x.id}`} onChange={(e) => updateTxn(x.id, { skip: e.target.checked })} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

export default function BankImport() {
  const { t } = useT();
  const { hasPermission } = useAuth();
  const [openId, setOpenId] = useState(null);
  const canUpload = hasPermission("upload_bank_statement");
  const canApprove = hasPermission("approve_bank_import");

  return (
    <div data-testid="bank-import-page">
      <PageHeader title={t("Bank Statement Intelligence")} subtitle={t("Upload, auto-categorize and approve into finance")} icon={Landmark}>
        {openId && <Badge tone="info" className="gap-1"><Sparkles size={12} /> AI</Badge>}
      </PageHeader>
      {openId
        ? <StatementDetail id={openId} onBack={() => setOpenId(null)} canApprove={canApprove} />
        : <StatementList onOpen={setOpenId} canUpload={canUpload} />}
    </div>
  );
}
